import React from 'react';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import Icon from '../icon';
import styled from 'styled-components/native';
import TextGeneric from '../textGeneric';
import {SideMenuListItemProps} from '../../interfaces/components/sideMenuListItem';
import MyPressable from '../myPressable';
import {I18nManager} from 'react-native';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';

const Container = styled(MyPressable)<{
  paddingHorizontal?: number;
  paddingVertical?: number;
}>`
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-horizontal: ${({paddingHorizontal}) => paddingHorizontal || 5}%;
  padding-vertical: ${({paddingVertical}) => paddingVertical || 3}%;
`;
const LeftIconContainer = styled.View`
  background-color: ${({theme}) => theme.text.light};
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  padding: ${({theme}) => theme.text.s5 * 0.7}px;
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 1px 2px;
  shadow-opacity: 0.05;
  shadow-radius: 4px;
  border-width: 0.4px;
  border-color: ${({theme}) => theme.text.dark + '11'};
`;
const LeftContainer = styled.View`
  flex-direction: row;
  flex: 1;
  align-items: center;
  justify-content: flex-start;
`;
const RightIconContainer = styled.View`
  align-self: center;
  justify-content: center;
  padding-end: 1%;
  transform: ${I18nManager.isRTL ? 'rotate(180deg)' : 'rotate(0deg)'};
`;
const Title = styled(TextGeneric)<{paddingHorizontal: number}>`
  font-size: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.text.dark};
  padding-horizontal: ${({paddingHorizontal}) => paddingHorizontal}px;
`;

/**
 * A Side Menu List Item component
 *
 * @component
 *
 * @param {ViewStyle} style - The container style of the component.
 * @param {string} iconName - The icon name for the component
 * @param {number} iconSize - The icon size in pixel unit, Default value is [23px]
 * @param iconStyle
 * @param {string} title - The Title of the component.
 * @param {function} onPress  - The press action for the component.
 * @param {boolean} showArrow - show the chevron Left icon
 * @constructor
 *
 * @returns {React.ReactElement} The rendered Side Menu List Item component.
 *
 */
const SideMenuListItem: React.FC<SideMenuListItemProps> = ({
  style,
  iconName,
  iconSize,
  iconStyle,
  title,
  onPress,
  showArrow,
}: SideMenuListItemProps): React.ReactElement => {
  /**
   * Represents the current theme.
   * @type {Theme}
   */
  const theme = useTheme() as Theme;
  const {wdp, getByScreenSize} = useResponsiveDimensions();

  return (
    <Container
      onPress={onPress}
      containerStyle={style}
      paddingHorizontal={getByScreenSize(5, 5)}
      paddingVertical={getByScreenSize(3, 1)}>
      <LeftContainer>
        <LeftIconContainer style={iconStyle}>
          <Icon
            name={iconName}
            size={iconSize || getByScreenSize(theme.text.s5, theme.text.s8)}
            color={theme.secondary + 'AA'}
          />
        </LeftIconContainer>
        <Title paddingHorizontal={wdp(getByScreenSize(5, 1))}>{title}</Title>
      </LeftContainer>
      {!!showArrow && (
        <RightIconContainer>
          <Icon
            name={'arrowRight'}
            size={iconSize || theme.text.s55}
            color={theme.menu.nav}
          />
        </RightIconContainer>
      )}
    </Container>
  );
};

export default SideMenuListItem;
